import { SETTINGS_PERMISSIONS_MATRIX_COLUMNS } from "../constants/columns.ts"
import type {
  ActionRecord,
  ColumnKey,
  PermissionsFlat,
  SectionRecord
} from "../types.ts"
import type {
  PermissionDisplayMap,
  PermissionFeatures
} from "../types/permissions.types.ts"
import {
  type SectionLayout as Placement,
  VIEW_ALL_CODES,
  buildSectionLayouts
} from "./section-layout.ts"
import { getColKey, getDisplayEntry, getSectionTitle } from "./utils.ts"

export type MatrixColumn = (typeof SETTINGS_PERMISSIONS_MATRIX_COLUMNS)[number]

export interface PermissionRef {
  sectionKey: string
  subKey: string
  actionKey: string
  label: string
}

export interface MatrixRow {
  // The section the row's rights are stored under, which is not always the
  // section showing it: Files shows the Documents row stored under Projects.
  sectionKey: string
  subKey: string
  label: string
  cells: Partial<Record<ColumnKey, string>>
  extras: PermissionRef[]
  actions: ActionRecord
}

// Another module's rights, shown read-only where they also decide access:
// Locations shows a lead's location to whoever Leads lets see the lead.
export interface InheritedRow {
  sectionKey: string
  subKey: string
  label: string
  sourceTitle: string
  cells: Partial<Record<ColumnKey, string>>
}

export type AdditionalGroupKey =
  | "files"
  | "locations"
  | "signatures"
  | "approvals"
  | "team"
  | "financial"
  | "lifecycle"
  | "advanced"

export interface AdditionalGroup {
  key: AdditionalGroupKey
  items: PermissionRef[]
}

export interface SectionLayout {
  key: string
  title: string
  visibility: PermissionRef[]
  columns: readonly MatrixColumn[]
  rows: MatrixRow[]
  inherited: InheritedRow[]
  // Rights of a section with no create/view/edit/delete grid at all
  // (e-signature, communication), listed as plain toggles.
  actions: PermissionRef[]
  additional: AdditionalGroup[]
  // What "Select all additional" turns on: never one of the new View All
  // rights, which only their own checkbox may grant.
  bulkFeatures: PermissionRef[]
}

const ADDITIONAL_GROUPS: ReadonlyArray<{
  key: AdditionalGroupKey
  codes: readonly string[]
}> = [
  {
    key: "files",
    codes: [
      "files_share",
      "files_customer_visibility",
      "files_request_review",
      "document_review",
      "files_restore",
      "files_permanent_delete",
      "project_doc_manage_others"
    ]
  },
  {
    key: "locations",
    codes: [
      "global_map_analytics_view",
      "project_view_exact_location",
      "lead_view_exact_location",
      "locations_export"
    ]
  },
  {
    key: "signatures",
    codes: [
      "document_send_for_signature",
      "esign_resend",
      "document_void",
      "document_countersign",
      "document_view_activity",
      "esign_download_signed",
      "esign_manage_others"
    ]
  },
  {
    key: "approvals",
    codes: [
      "project_approval",
      "project_budget_approval",
      "lead_proposal_approval",
      "lead_contract_approval"
    ]
  },
  {
    key: "team",
    codes: [
      "project_assignable_as_pm",
      "project_assign_pm",
      "lead_assignable_as_sales",
      "lead_assign_sales_manager",
      "calendar_assign_to_others",
      "calendar_assignable_as_participant",
      "customer_invite",
      "subcontractor_invite",
      "team_invite_user",
      "team_modify_permissions",
      "team_admin_notifications",
      "team_manage_account_deletion"
    ]
  },
  {
    key: "financial",
    codes: [
      "project_snapshot_sales",
      "project_snapshot_commissions",
      "team_performance_view"
    ]
  },
  {
    key: "lifecycle",
    codes: [
      "project_change_status",
      "project_close",
      "project_archive",
      "project_view_archive",
      "lead_view_archive"
    ]
  },
  // Anything the server adds that no group names lands here, so a new right is
  // never left without a control.
  {
    key: "advanced",
    codes: ["project_activity_log", "lead_activity_log"]
  }
]

const FEATURES_KEY = "features"

export const isVisibilityKey = (actionKey: string) =>
  actionKey.endsWith("_view_all")

const humanize = (key: string) => {
  const text = key.replace(/_/g, " ")
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const getPermissionLabel = (
  actionKey: string,
  displayMap?: PermissionDisplayMap | null,
  subKey?: string
) => {
  const name = getDisplayEntry(displayMap, actionKey)?.name
  if (name) return name
  const stripped =
    subKey && actionKey.startsWith(`${subKey}_`)
      ? actionKey.slice(subKey.length + 1)
      : actionKey
  return humanize(stripped)
}

export const getRowLabel = (
  subKey: string,
  displayMap?: PermissionDisplayMap | null
) => getDisplayEntry(displayMap, subKey)?.name ?? humanize(subKey)

export const getSectionLabel = (
  sectionKey: string,
  displayMap?: PermissionDisplayMap | null
) => {
  const title = getSectionTitle(
    sectionKey as keyof PermissionFeatures,
    displayMap
  )
  return title.charAt(0).toUpperCase() + title.slice(1)
}

const groupAdditional = (refs: PermissionRef[]): AdditionalGroup[] => {
  const byGroup = new Map<AdditionalGroupKey, PermissionRef[]>()
  refs.forEach((ref) => {
    const group =
      ADDITIONAL_GROUPS.find((g) => g.codes.includes(ref.actionKey))?.key ??
      "advanced"
    byGroup.set(group, [...(byGroup.get(group) ?? []), ref])
  })
  return ADDITIONAL_GROUPS.filter((g) => byGroup.has(g.key)).map((g) => ({
    key: g.key,
    items: byGroup.get(g.key) ?? []
  }))
}

const cellsOf = (actions: ActionRecord) => {
  const cells: Partial<Record<ColumnKey, string>> = {}
  const rest: string[] = []
  Object.keys(actions).forEach((actionKey) => {
    const col = getColKey(actionKey, actions)
    if (col) cells[col] = actionKey
    else rest.push(actionKey)
  })
  return { cells, rest }
}

export function getSectionLayout(
  sectionKey: string,
  section: SectionRecord,
  displayMap?: PermissionDisplayMap | null,
  placement?: Placement
): SectionLayout {
  const visibility: PermissionRef[] = []
  const featureRefs: PermissionRef[] = []
  const rows: MatrixRow[] = []
  const present = new Set<ColumnKey>()
  const hiddenRows = new Set(placement?.hiddenSubKeys ?? [])
  const hiddenCodes = new Set(placement?.hiddenCodes ?? [])

  const toRef = (
    subKey: string,
    actionKey: string,
    storedIn = sectionKey
  ): PermissionRef => ({
    sectionKey: storedIn,
    subKey,
    actionKey,
    label: getPermissionLabel(actionKey, displayMap, subKey)
  })

  const addRow = (
    storedIn: string,
    subKey: string,
    actions: ActionRecord,
    label = getRowLabel(subKey, displayMap)
  ) => {
    const { cells, rest } = cellsOf(actions)
    const row: MatrixRow = {
      sectionKey: storedIn,
      subKey,
      label,
      cells,
      extras: [],
      actions
    }
    ;(Object.keys(cells) as ColumnKey[]).forEach((col) => present.add(col))
    rest.forEach((actionKey) =>
      (isVisibilityKey(actionKey) ? visibility : row.extras).push(
        toRef(subKey, actionKey, storedIn)
      )
    )
    rows.push(row)
  }

  placement?.linkedRows.forEach((row) =>
    addRow(row.parentKey, row.subKey, row.actions, row.label)
  )

  Object.entries(section).forEach(([subKey, actions]) => {
    if (hiddenRows.has(subKey)) return
    const shown = Object.fromEntries(
      Object.entries(actions as ActionRecord).filter(
        ([code]) => !hiddenCodes.has(code)
      )
    )

    if (subKey === FEATURES_KEY) {
      Object.keys(shown).forEach((actionKey) =>
        (isVisibilityKey(actionKey) ? visibility : featureRefs).push(
          toRef(subKey, actionKey)
        )
      )
      return
    }
    addRow(sectionKey, subKey, shown)
  })

  placement?.linkedFeatures.forEach((feature) =>
    (isVisibilityKey(feature.key) ? visibility : featureRefs).push(
      toRef(feature.subKey, feature.key, feature.parentKey)
    )
  )

  const order = placement?.featureOrder
  if (order) {
    const rank = (ref: PermissionRef) => {
      const at = order.indexOf(ref.actionKey)
      return at === -1 ? order.length : at
    }
    featureRefs.sort((a, b) => rank(a) - rank(b))
  }

  const inherited: InheritedRow[] = (placement?.inherited ?? []).map((row) => ({
    sectionKey: row.parentKey,
    subKey: row.subKey,
    label: row.label,
    sourceTitle: getSectionLabel(row.sourceKey, displayMap),
    cells: cellsOf(row.actions).cells
  }))

  const hasGrid = present.size > 0
  const columns = hasGrid
    ? SETTINGS_PERMISSIONS_MATRIX_COLUMNS.filter(
        (col) => col.key !== "full_access" || present.has("full_access")
      )
    : []
  const additional = groupAdditional(featureRefs)

  return {
    key: sectionKey,
    title: getSectionLabel(sectionKey, displayMap),
    visibility,
    columns,
    rows: hasGrid ? rows : [],
    inherited: hasGrid ? inherited : [],
    actions: hasGrid ? [] : rows.flatMap((row) => row.extras),
    additional,
    bulkFeatures: [
      ...visibility.filter(
        (ref) =>
          ref.subKey === FEATURES_KEY && !VIEW_ALL_CODES.has(ref.actionKey)
      ),
      ...additional.flatMap((group) => group.items)
    ]
  }
}

/** Every section the payload serves, with rights relocated between them. */
export const getSectionLayouts = (
  sections: PermissionsFlat,
  displayMap?: PermissionDisplayMap | null
): SectionLayout[] => {
  const placements = buildSectionLayouts(sections)
  return Object.entries(sections).map(([key, section]) =>
    getSectionLayout(
      key,
      section,
      displayMap,
      placements[key as keyof PermissionsFlat]
    )
  )
}

export const getRowDisclosureId = (sectionKey: string, subKey: string) =>
  `row:${sectionKey}:${subKey}`

export const getGroupDisclosureId = (
  sectionKey: string,
  groupKey: AdditionalGroupKey
) => `group:${sectionKey}:${groupKey}`

export interface SearchResult extends PermissionRef {
  // The section whose panel shows the control, which can differ from the
  // section that stores it.
  panelKey: string
  sectionTitle: string
  // The disclosure that has to be open for the control to be on screen.
  disclosureId?: string
}

export function buildSearchIndex(
  layouts: SectionLayout[],
  columnLabel: (col: ColumnKey) => string
): SearchResult[] {
  return layouts.flatMap((layout) => {
    const base = { panelKey: layout.key, sectionTitle: layout.title }

    return [
      ...layout.visibility.map((ref) => ({ ...ref, ...base })),
      ...layout.rows.flatMap((row) => [
        ...Object.entries(row.cells).map(([col, actionKey]) => ({
          ...base,
          sectionKey: row.sectionKey,
          subKey: row.subKey,
          actionKey: actionKey as string,
          label: `${row.label} — ${columnLabel(col as ColumnKey)}`
        })),
        ...row.extras.map((ref) => ({
          ...ref,
          ...base,
          label: `${row.label} — ${ref.label}`,
          disclosureId: getRowDisclosureId(layout.key, row.subKey)
        }))
      ]),
      ...layout.actions.map((ref) => ({ ...ref, ...base })),
      ...layout.additional.flatMap((group) =>
        group.items.map((ref) => ({
          ...ref,
          ...base,
          disclosureId: getGroupDisclosureId(layout.key, group.key)
        }))
      )
    ]
  })
}

export function searchPermissions(
  index: SearchResult[],
  query: string
): SearchResult[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return []
  return index.filter((result) => {
    const haystack =
      `${result.sectionTitle} ${result.label} ${result.actionKey}`.toLowerCase()
    return terms.every((term) => haystack.includes(term))
  })
}

export const getPermissionDomId = (actionKey: string) =>
  `permission-control-${actionKey}`
